/**
 * Adaptateur Supabase de la console staff.
 *
 * Il ne prend pas de `DbClientFactory` : le staff n'a pas d'`ExecutionContext`,
 * n'appartenant à aucun tenant. Il reçoit le client de la session staff, et
 * n'appelle que des fonctions `staff_*` — chacune vérifie en base que le
 * compte est staff, actif, et que son jeton est passé par la MFA.
 *
 * Aucun `from()` sur une table métier ici, à dessein : la console lit des
 * agrégats. Lire les données d'un client demande une session de support, et
 * se fait alors avec les RLS ordinaires.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  SignupGrant,
  StaffIdentity,
  StaffTenantTermination,
  SupportSession,
  SupportVisitRepository,
  TenantAiUsage,
  TenantClosure,
  TenantOverview,
  TenantPlan,
} from "@dker/core";
import { DomainError, isTenantPlan, staffErrorMessage } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

function fail(error: { message: string }): never {
  throw new Error(staffErrorMessage(error.message));
}

function toOverview(row: Row): TenantOverview {
  return {
    tenantId: row.tenant_id,
    name: row.name,
    tradeType: row.trade_type ?? null,
    plan: isTenantPlan(row.plan) ? row.plan : "essential",
    planStartedAt: row.plan_started_at ?? null,
    createdAt: row.created_at,
    einvoiceEnabled: Boolean(row.einvoice_enabled),
    activeUsers: Number(row.active_users ?? 0),
    lastSignInAt: row.last_sign_in_at ?? null,
    quotes30d: Number(row.quotes_30d ?? 0),
    invoices30d: Number(row.invoices_30d ?? 0),
    sendsFailed30d: Number(row.sends_failed_30d ?? 0),
    aiCalls30d: Number(row.ai_calls_30d ?? 0),
    aiCost30d: Number(row.ai_cost_30d ?? 0),
    aiUnpriced30d: Number(row.ai_unpriced_30d ?? 0),
    supportOpen: Boolean(row.support_open),
  };
}

function toUsage(row: Row): TenantAiUsage {
  return {
    month: row.month,
    feature: row.feature,
    model: row.model,
    calls: Number(row.calls ?? 0),
    inputTokens: Number(row.input_tokens ?? 0),
    outputTokens: Number(row.output_tokens ?? 0),
    cost: Number(row.cost ?? 0),
    unpriced: Number(row.unpriced ?? 0),
  };
}

function toSession(row: Row): SupportSession {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    tenantName: row.tenant_name,
    staffName: row.staff_name,
    isMine: Boolean(row.is_mine),
    reason: row.reason,
    startedAt: row.started_at,
    expiresAt: row.expires_at,
    endedAt: row.ended_at ?? null,
  };
}

export interface StaffConsole {
  /** `null` si le compte connecté n'est pas un staff actif. */
  whoami(userId: string): Promise<StaffIdentity | null>;
  tenants(): Promise<TenantOverview[]>;
  aiUsage(tenantId: string, months?: number): Promise<TenantAiUsage[]>;
  sessions(tenantId?: string): Promise<SupportSession[]>;
  beginSupport(tenantId: string, reason: string, minutes: number): Promise<string>;
  endSupport(): Promise<void>;
  setPlan(tenantId: string, plan: TenantPlan): Promise<void>;
  /** La résiliation en cours, ou `null`. */
  termination(tenantId: string): Promise<StaffTenantTermination | null>;
  /** Pose ou déplace la date d'effet (AAAA-MM-JJ). Admin seul. */
  terminate(tenantId: string, effectiveOn: string, note: string | null): Promise<void>;
  cancelTermination(tenantId: string): Promise<void>;
  closures(): Promise<TenantClosure[]>;
  /** Les autorisations de mise en service, en attente d'abord. */
  signupGrants(): Promise<SignupGrant[]>;
  /** Autorise l'adresse à créer une entreprise. Admin seul. */
  grantSignup(email: string, note: string | null): Promise<void>;
  /** Retire une autorisation qui n'a pas encore servi. Admin seul. */
  revokeSignupGrant(email: string): Promise<void>;
}

export function createSupabaseStaffConsole(client: SupabaseClient): StaffConsole {
  return {
    async whoami(userId) {
      // `staff_reads_self` : un compte ne lit que sa propre fiche.
      const { data, error } = await client
        .from("staff_users")
        .select("id, full_name, role, is_active")
        .eq("id", userId)
        .maybeSingle();
      if (error) fail(error);
      if (!data || !data.is_active) return null;
      return { id: data.id, fullName: data.full_name, role: data.role };
    },

    async tenants() {
      const { data, error } = await client.rpc("staff_tenants_overview");
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toOverview);
    },

    async aiUsage(tenantId, months = 6) {
      const { data, error } = await client.rpc("staff_tenant_ai_usage", {
        p_tenant_id: tenantId,
        p_months: months,
      });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toUsage);
    },

    async sessions(tenantId) {
      const { data, error } = await client.rpc("staff_support_sessions", {
        p_tenant_id: tenantId ?? null,
      });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toSession);
    },

    async beginSupport(tenantId, reason, minutes) {
      const { data, error } = await client.rpc("staff_begin_impersonation", {
        p_tenant_id: tenantId,
        p_reason: reason,
        p_minutes: minutes,
      });
      if (error) fail(error);
      return data as string;
    },

    async endSupport() {
      const { error } = await client.rpc("staff_end_impersonation");
      if (error) fail(error);
    },

    async setPlan(tenantId, plan) {
      const { error } = await client.rpc("staff_set_tenant_plan", {
        p_tenant_id: tenantId,
        p_plan: plan,
      });
      if (error) fail(error);
    },

    async termination(tenantId) {
      const { data, error } = await client.rpc("staff_tenant_termination", {
        p_tenant_id: tenantId,
      });
      if (error) fail(error);
      const row = ((data ?? []) as Row[])[0];
      if (!row) return null;
      return {
        effectiveOn: row.effective_on,
        purgeOn: row.purge_on,
        readOnly: Boolean(row.read_only),
        requestedAt: row.requested_at,
        requestedByName: row.requested_by_name ?? null,
        note: row.note ?? null,
        exports: Number(row.exports ?? 0),
      };
    },

    async terminate(tenantId, effectiveOn, note) {
      const { error } = await client.rpc("staff_terminate_tenant", {
        p_tenant_id: tenantId,
        p_effective_on: effectiveOn,
        p_note: note,
      });
      if (error) fail(error);
    },

    async cancelTermination(tenantId) {
      const { error } = await client.rpc("staff_cancel_termination", { p_tenant_id: tenantId });
      if (error) fail(error);
    },

    async closures() {
      const { data, error } = await client.rpc("staff_tenant_closures");
      if (error) fail(error);
      return ((data ?? []) as Row[]).map((row) => ({
        tenantId: row.tenant_id,
        name: row.name,
        siret: row.siret ?? null,
        effectiveOn: row.effective_on,
        requestedAt: row.requested_at,
        requestedByName: row.requested_by_name ?? null,
        exports: Number(row.exports ?? 0),
        purgedAt: row.purged_at,
      }));
    },

    async signupGrants() {
      const { data, error } = await client.rpc("staff_signup_grants");
      if (error) fail(error);
      return ((data ?? []) as Row[]).map((row) => ({
        email: row.email,
        note: row.note ?? null,
        createdAt: row.created_at,
        createdByName: row.created_by_name ?? null,
        claimedAt: row.claimed_at ?? null,
        tenantId: row.tenant_id ?? null,
        tenantName: row.tenant_name ?? null,
      }));
    },

    async grantSignup(email, note) {
      const { error } = await client.rpc("staff_grant_signup", { p_email: email, p_note: note });
      if (error) fail(error);
    },

    async revokeSignupGrant(email) {
      const { error } = await client.rpc("staff_revoke_signup_grant", { p_email: email });
      if (error) fail(error);
    },
  };
}

/**
 * Côté client : les sessions de support ouvertes chez le patron connecté.
 * `my_support_sessions()` ne rend rien à un autre rôle — la base tranche,
 * le service ne fait que le redire.
 */
export function createSupabaseSupportVisitRepository(
  clientFor: DbClientFactory,
): SupportVisitRepository {
  return {
    async list(ctx) {
      const { data, error } = await clientFor(ctx).rpc("my_support_sessions");
      if (error) {
        throw new DomainError("conflict", `Lecture refusée par la base : ${error.message}`, {
          pgCode: error.code,
        });
      }
      return ((data ?? []) as Row[]).map((row) => ({
        id: row.id,
        staffName: row.staff_name,
        reason: row.reason,
        startedAt: row.started_at,
        expiresAt: row.expires_at,
        endedAt: row.ended_at ?? null,
      }));
    },
  };
}
